import { Ref, ref } from "vue";

import type { Controller } from "@ctrl/controller-rpc";

import type { UseProfile } from "@/composables/useProfile";
import { sentry } from "@/utils/sentry";
import { userReportEventUrl } from "@/utils/sentry/common";
import { property } from "@/utils/unknown";

/** Minimum time to show the pre-open upload modal so it does not flash. */
const UPLOAD_MODAL_MIN_MS = 2000;
const FEATUREBASE_EVENT = "featurebase:widget";

declare global {
  interface Window {
    __routineFeaturebaseListenerInstalled?: boolean;
  }
}

function updateFeaturebaseMetadata(metadata: Record<string, string>): void {
  window.postMessage({
    target: "FeaturebaseWidget",
    data: {
      action: "updateMetadata",
      metadata,
    },
  });
}

function openFeaturebaseWidget(): void {
  window.postMessage(
    {
      target: "FeaturebaseWidget",
      data: {
        action: "openFeedbackWidget",
      },
    },
    "*"
  );
}

export const UseFeedback = (function () {
  type Instance = ReturnType<typeof Singleton>;
  let instance: Instance | null = null;

  function Singleton(
    _controller: Controller,
    profileRef: Ref<UseProfile>
  ): Promise<{
    isOpen: Ref<boolean>;
    isUploading: Ref<boolean>;
    prepare: () => Promise<void>;
    open: () => Promise<void>;
    close: () => void;
  }> {
    const isOpen = ref(false);
    const isUploading = ref(false);
    const sentryEventId = ref<string | null>(null);
    /** Settled upload for the current widget session; used to attach User Feedback. */
    let sentryUpload: Promise<string | null> = Promise.resolve(null);
    /** Bumped on each open so a superseded in-flight upload does not open the widget. */
    let uploadGeneration = 0;

    const getUserNameAndEmail = () => {
      const user = profileRef.value.user;
      const name =
        user.givenName || user.familyName
          ? `${user.givenName} ${user.familyName}`.trim()
          : user.email;
      return { name, email: user.email };
    };

    const open = async (): Promise<void> => {
      // Upload logs and wait for Sentry ingest before opening Featurebase, so
      // the widget only receives a sentryId that already exists.
      const generation = ++uploadGeneration;
      const { name } = getUserNameAndEmail();

      const stillCurrent = () => generation === uploadGeneration;

      if (!sentry.isInitialized()) {
        sentryEventId.value = null;
        sentryUpload = Promise.resolve(null);
        updateFeaturebaseMetadata({ sentryUploadFailed: "true" });
        if (stillCurrent()) openFeaturebaseWidget();
        return;
      }

      const eventId = sentry.newEventId();
      const startedAt = Date.now();
      isUploading.value = true;
      let uploadedEventId: string | null = null;
      try {
        const uploaded = await sentry.captureEvent(name, eventId);
        if (uploaded) uploadedEventId = eventId;
      } catch (e) {
        window.console.warn("Failed to prepare Sentry user report:", e);
      } finally {
        if (stillCurrent()) {
          const remaining = UPLOAD_MODAL_MIN_MS - (Date.now() - startedAt);
          if (remaining > 0) {
            await new Promise<void>((resolve) =>
              setTimeout(resolve, remaining)
            );
          }
          if (stillCurrent()) isUploading.value = false;
        }
      }

      if (!stillCurrent()) return;

      sentryEventId.value = uploadedEventId;
      sentryUpload = Promise.resolve(uploadedEventId);
      if (uploadedEventId) {
        updateFeaturebaseMetadata({
          sentryId: uploadedEventId,
          sentryURL: userReportEventUrl(uploadedEventId),
        });
      } else {
        updateFeaturebaseMetadata({ sentryUploadFailed: "true" });
      }
      openFeaturebaseWidget();
    };

    const prepare = async (): Promise<void> => {
      isOpen.value = true;
      await open();
    };

    const handleFeaturebaseEvent = async (event: Event) => {
      if (!(event instanceof CustomEvent)) return;
      const detail: unknown = event.detail;
      const action = property(detail, "action");

      if (action !== "feedbackSubmitted") return;

      // Pull a reasonable title from the payload if available.
      const post = property(detail, "post");
      const titleValue = [
        property(post, "title"),
        property(post, "name"),
        property(post, "subject"),
      ].find((value) => typeof value === "string" && value.length > 0);
      const title = typeof titleValue === "string" ? titleValue : "";

      if (!sentry.isInitialized()) return;

      // Capture the upload destined for this submission before any later open()
      // can bump uploadGeneration / replace sentryUpload.
      const uploadForSubmit = sentryUpload;
      const generationForSubmit = uploadGeneration;
      sentryEventId.value = null;
      sentryUpload = Promise.resolve(null);

      try {
        const uploadedEventId = await uploadForSubmit;
        if (generationForSubmit !== uploadGeneration) return;
        if (!uploadedEventId) return;

        const { name, email } = getUserNameAndEmail();
        await sentry.captureUserFeedback(uploadedEventId, name, email, title);
      } catch (e) {
        window.console.warn("Failed to capture Sentry feedback:", e);
      }
    };

    // Install the Featurebase event listener once (singleton).
    if (!window.__routineFeaturebaseListenerInstalled) {
      window.__routineFeaturebaseListenerInstalled = true;
      window.addEventListener(FEATUREBASE_EVENT, handleFeaturebaseEvent);
    } else {
      // Ensure this instance also reacts (in case of hot reloads).
      window.removeEventListener(FEATUREBASE_EVENT, handleFeaturebaseEvent);
      window.addEventListener(FEATUREBASE_EVENT, handleFeaturebaseEvent);
    }

    const close = () => {
      isOpen.value = false;
      // Featurebase widget handles closing automatically; no specific close action needed.
    };

    return Promise.resolve({
      close,
      isOpen,
      isUploading,
      open,
      prepare,
    });
  }

  return {
    setInstance: function (
      controller: Controller,
      profile: Ref<UseProfile>
    ): Instance {
      if (!instance) instance = Singleton(controller, profile);
      return instance;
    },
    getInstance: function () {
      if (instance) return instance;
      else throw "UseFeedback Singleton is not initialized";
    },
  };
})();
